import { useState, useEffect } from "react";

// ─── Diary utilities ──────────────────────────────────────────────────────────

const SAT_LABELS = ['','Very Unsatisfied','Unsatisfied','Somewhat Unsatisfied','Slightly Unsatisfied','Neutral','Slightly Satisfied','Somewhat Satisfied','Satisfied','Very Satisfied','Extremely Satisfied'];

function satColor(n) {
  if (n <= 3) return { bg: '#fdecea', text: '#c0392b' };
  if (n <= 5) return { bg: '#fef3e2', text: '#d35400' };
  if (n <= 7) return { bg: '#eafaf1', text: '#1e8449' };
  return { bg: '#d5f5e3', text: '#1a6b3c' };
}

function calcDuration(on, off) {
  if (!on || !off) return null;
  const [h1, m1] = on.split(':').map(Number);
  const [h2, m2] = off.split(':').map(Number);
  let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (mins < 0) mins += 1440;
  if (mins === 0) return null;
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
}

function calcDurationMins(on, off) {
  if (!on || !off) return 0;
  const [h1, m1] = on.split(':').map(Number);
  const [h2, m2] = off.split(':').map(Number);
  let mins = (h2 * 60 + m2) - (h1 * 60 + m1);
  if (mins < 0) mins += 1440;
  return mins;
}

function fmtMins(mins) {
  if (mins === 0) return '0m';
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
}

function fmtDate(v) {
  if (!v) return '—';
  const [y, m, d] = v.split('-');
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtTime(v) {
  if (!v) return '—';
  const [h, m] = v.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

const today = () => new Date().toISOString().split('T')[0];
const emptyForm = () => ({ need: '', source: '', day: today(), timeOn: '', timeOff: '', satisfaction: 5, notes: '' });

const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontFamily: 'Arial, sans-serif', fontSize: '0.95rem', background: '#fafafa', color: '#2c2c2c', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.6px', color: '#7a7a7a', marginBottom: 6 };


// ─── Emotion Quiz ────────────────────────────────────────────────────────────
/* eslint-disable no-unused-vars */

const QUESTIONS = [
  // Section 1: Emotional Triggers in News
  {
    id: "q1",
    section: "Your Emotional Triggers",
    sectionDesc: "How different types of news make you feel",
    text: "When you encounter a news story about a threat to your community (crime, disease, economic downturn), you most often feel:",
    options: [
      { label: "Fear / anxiety — I want to know more to protect myself", value: "fear_seek" },
      { label: "Anger — Someone is responsible and should be held accountable", value: "anger_share" },
      { label: "Overwhelmed — I often close the tab or scroll past", value: "overwhelm_avoid" },
      { label: "Concern but calm — I look for what I can actually do", value: "concern_act" },
    ],
  },
  {
    id: "q2",
    section: "Your Emotional Triggers",
    sectionDesc: "How different types of news make you feel",
    text: "A story about political opponents doing something you find deeply wrong tends to make you:",
    options: [
      { label: "Furious — I want to share it widely so others know", value: "anger_share" },
      { label: "Disgusted — I want to disengage entirely", value: "disgust_avoid" },
      { label: "Anxious — I worry about what it means for the future", value: "fear_seek" },
      { label: "Skeptical — I look for more context before reacting", value: "skeptic_seek" },
    ],
  },
  {
    id: "q3",
    section: "Your Emotional Triggers",
    sectionDesc: "How different types of news make you feel",
    text: "When you see a story about a large-scale tragedy (natural disaster, mass casualty event), your first impulse is:",
    options: [
      { label: "Sadness — I sit with it and sometimes discuss it with someone close", value: "sad_discuss" },
      { label: "Helplessness — I feel like nothing I do matters", value: "sad_avoid" },
      { label: "Urgency — I share it because people need to know", value: "fear_share" },
      { label: "Motivated — I look for ways to donate or help", value: "concern_act" },
    ],
  },
  {
    id: "q4",
    section: "Your Emotional Triggers",
    sectionDesc: "How different types of news make you feel",
    text: "Stories that make you feel awe or inspiration (scientific breakthroughs, acts of courage) typically cause you to:",
    options: [
      { label: "Share immediately — I love spreading good news", value: "awe_share" },
      { label: "Feel briefly uplifted, then return to the negative stuff", value: "awe_brief" },
      { label: "Dig deeper — I want to understand the full story", value: "awe_seek" },
      { label: "Feel skeptical — these stories often feel too good to be true", value: "skeptic_seek" },
    ],
  },
  // Section 2: News Consumption Habits
  {
    id: "q5",
    section: "Your News Habits",
    sectionDesc: "How you actually consume news day to day",
    text: "Describe your typical daily news scrolling:",
    options: [
      { label: "I scroll frequently throughout the day, often checking multiple times per hour", value: "heavy_scroll" },
      { label: "I check a few set times per day and then stop", value: "structured_scroll" },
      { label: "I avoid news most days and catch up when I feel ready", value: "avoidant_scroll" },
      { label: "I follow specific topics or events rather than scrolling broadly", value: "targeted_scroll" },
    ],
  },
  {
    id: "q6",
    section: "Your News Habits",
    sectionDesc: "How you actually consume news day to day",
    text: "When you share a news story, what's usually driving it?",
    options: [
      { label: "Outrage — I want others to be as angry as I am", value: "anger_share" },
      { label: "Concern — I think people I know need this information", value: "fear_share" },
      { label: "Awe or delight — it's remarkable and I want others to experience it", value: "awe_share" },
      { label: "I rarely share news — I'm not sure it changes anything", value: "share_avoid" },
    ],
  },
  {
    id: "q7",
    section: "Your News Habits",
    sectionDesc: "How you actually consume news day to day",
    text: "What kinds of news do you most reliably avoid or click away from?",
    options: [
      { label: "Violent or graphic content — it lingers in my mind too long", value: "disgust_avoid" },
      { label: "Politically charged content — it raises my blood pressure", value: "anger_avoid" },
      { label: "Sad or tragic stories — I feel too helpless", value: "sad_avoid" },
      { label: "I don't actively avoid specific types — I try to engage with all of it", value: "no_avoid" },
    ],
  },
  {
    id: "q8",
    section: "Your News Habits",
    sectionDesc: "How you actually consume news day to day",
    text: "After consuming a heavy dose of negative news, you typically:",
    options: [
      { label: "Feel worse and keep scrolling anyway — it's hard to stop", value: "heavy_scroll" },
      { label: "Put the phone down, but the feelings stay with me", value: "sad_avoid" },
      { label: "Want to talk to someone about what I read", value: "sad_discuss" },
      { label: "Consciously do something to reset — exercise, nature, etc.", value: "concern_act" },
    ],
  },
  // Section 3: Emotional → Action Links
  {
    id: "q9",
    section: "Emotion → Action",
    sectionDesc: "What emotions actually move you to do something",
    text: "When has news actually caused you to take a real-world action (donate, vote, call a representative, attend something)?",
    options: [
      { label: "When I was angry about an injustice", value: "anger_act" },
      { label: "When I was afraid of a real consequence for myself or loved ones", value: "fear_act" },
      { label: "When I felt hopeful that action would matter", value: "hope_act" },
      { label: "I can't recall news directly leading me to action", value: "no_act" },
    ],
  },
  {
    id: "q10",
    section: "Emotion → Action",
    sectionDesc: "What emotions actually move you to do something",
    text: "Which emotional state makes you most likely to discuss news with others (friends, family, colleagues)?",
    options: [
      { label: "Anger or frustration — I need to process it out loud", value: "anger_discuss" },
      { label: "Worry or anxiety — I'm looking for reassurance", value: "fear_discuss" },
      { label: "Excitement or curiosity — I want to share something interesting", value: "awe_discuss" },
      { label: "Sadness — talking helps me cope", value: "sad_discuss" },
    ],
  },
  {
    id: "q11",
    section: "Emotion → Action",
    sectionDesc: "What emotions actually move you to do something",
    text: "Which feeling most reliably stops you from acting on news — even when you feel you should?",
    options: [
      { label: "Overwhelm — there's too much and I don't know where to start", value: "overwhelm_avoid" },
      { label: "Helplessness — I don't think individuals can make a difference", value: "sad_avoid" },
      { label: "Distrust — I'm not sure the story is accurate or complete", value: "skeptic_seek" },
      { label: "Fatigue — I've been emotionally drained by the news for too long", value: "disgust_avoid" },
    ],
  },
  {
    id: "q12",
    section: "Emotion → Action",
    sectionDesc: "What emotions actually move you to do something",
    text: "When you think about your ideal relationship with news, what feels most true?",
    options: [
      { label: "I want to stay fully informed even if it costs me emotionally", value: "fear_seek" },
      { label: "I want to be informed but set stronger limits on how much I consume", value: "structured_scroll" },
      { label: "I want to consume news that leads me toward action, not just reaction", value: "concern_act" },
      { label: "I want to reconnect with news after a long period of avoidance", value: "avoidant_scroll" },
    ],
  },
];


function scoreAnswers(answers) {
  const counts = {};
  Object.values(answers).forEach((v) => {
    counts[v] = (counts[v] || 0) + 1;
  });

  // Derive dominant emotional driver
  const emotionMap = {
    fear_seek: "Fear/Anxiety",
    fear_share: "Fear/Anxiety",
    fear_discuss: "Fear/Anxiety",
    fear_act: "Fear/Anxiety",
    anger_share: "Anger",
    anger_avoid: "Anger",
    anger_discuss: "Anger",
    anger_act: "Anger",
    awe_share: "Awe/Hope",
    awe_seek: "Awe/Hope",
    awe_brief: "Awe/Hope",
    awe_discuss: "Awe/Hope",
    hope_act: "Awe/Hope",
    sad_discuss: "Sadness",
    sad_avoid: "Sadness",
    disgust_avoid: "Disgust",
    overwhelm_avoid: "Overwhelm",
    concern_act: "Motivated Concern",
    skeptic_seek: "Skepticism",
    no_act: "Disconnection",
    no_avoid: "Resilience",
    share_avoid: "Disconnection",
    heavy_scroll: "Anxiety Scrolling",
    structured_scroll: "Intentional Consumption",
    avoidant_scroll: "Avoidance",
    targeted_scroll: "Intentional Consumption",
  };

  const emotionCounts = {};
  Object.entries(counts).forEach(([key, val]) => {
    const emotion = emotionMap[key] || "Other";
    emotionCounts[emotion] = (emotionCounts[emotion] || 0) + val;
  });

  const dominant = Object.entries(emotionCounts).sort((a, b) => b[1] - a[1])[0][0];

  // Determine behavior tendencies
  const seekCount = Object.entries(counts).filter(([k]) => k.includes("seek")).reduce((a, [, v]) => a + v, 0);
  const shareCount = Object.entries(counts).filter(([k]) => k.includes("share")).reduce((a, [, v]) => a + v, 0);
  const avoidCount = Object.entries(counts).filter(([k]) => k.includes("avoid")).reduce((a, [, v]) => a + v, 0);
  const discussCount = Object.entries(counts).filter(([k]) => k.includes("discuss")).reduce((a, [, v]) => a + v, 0);
  const actCount = Object.entries(counts).filter(([k]) => k.includes("act")).reduce((a, [, v]) => a + v, 0);

  return { dominant, emotionCounts, seekCount, shareCount, avoidCount, discussCount, actCount, raw: counts };
}


const PROFILES = {
  "Fear/Anxiety": {
    title: "The Vigilant Monitor",
    emoji: "👁️",
    color: "#E8A838",
    description:
      "Fear and anxiety are your dominant emotional lens for news. You consume news as a protective behavior — staying informed feels like staying safe. Research shows fear-driven news consumers tend to seek out more information (especially threat-related), but can tip into information overload, which paradoxically leads to avoidance.",
    strengths: ["Deeply informed on topics that matter to you", "Early awareness of emerging risks", "Strong motivation to act when threats feel personal"],
    watchFor: ["Doomscrolling loops — fear activates seeking, which finds more fear", "Confusing staying informed with staying safe", "Anxiety fatigue leading to sudden avoidance after periods of heavy consumption"],
    habits: {
      scrolling: "You likely scroll frequently, especially during periods of uncertainty. Your feed has probably learned to surface threatening content because that's what keeps your attention.",
      sharing: "You share news to warn others — your sharing is altruistic but can amplify fear in your network.",
      avoiding: "You may avoid news episodically when the anxiety becomes unbearable, then return with even higher intensity.",
      discussing: "You discuss news to process worry and seek reassurance — conversations can spiral if others amplify rather than ground your concerns.",
      acting: "Fear can be a powerful action driver when the threat feels personal and proximate. Abstract or distant threats often produce paralysis instead.",
    },
    research: "Research by Link (2025) found fear, anxiety, and worry are the strongest predictors of information seeking — but also that information overload triggers avoidance cycles.",
    tips: [
      "Set specific 'news windows' — two or three times a day — rather than continuous monitoring",
      "After a fear-triggering story, ask: 'What's one specific action I could take?' This converts anxiety into agency",
      "Notice when you're seeking information to manage anxiety vs. to inform a decision",
      "Deliberately seek out solutions-oriented journalism to balance threat-heavy content",
    ],
  },
  Anger: {
    title: "The Engaged Challenger",
    emoji: "🔥",
    color: "#D94F3D",
    description:
      "Anger is your dominant news emotion. Of all emotions, anger is the most reliably linked to sharing and action — which makes you a powerful force in your information network. Research consistently shows anger-driven content spreads further and faster than any other emotion. The question is whether that energy gets directed productively.",
    strengths: ["High motivation to act on injustice", "Natural advocate and mobilizer in your community", "Less likely to be passive in the face of wrongdoing"],
    watchFor: ["Sharing before verifying — anger speeds up the share impulse and bypasses critical evaluation", "Outrage fatigue — sustained anger without resolution becomes numbness", "The algorithm is feeding you more anger because it keeps you engaged"],
    habits: {
      scrolling: "You likely spend significant time in politically or socially charged news. Your engagement signals (comments, shares, reactions) have trained algorithms to deliver more rage-inducing content.",
      sharing: "You are among the most active sharers in your network. Research shows anger-driven tweets travel further and deeper than anxiety-driven ones. This is your superpower and your risk.",
      avoiding: "You may avoid news that makes you feel hopeless or sad, but find it hard to avoid content that makes you angry — it's paradoxically harder to disengage from.",
      discussing: "You discuss news with intensity and may dominate conversations. You process by externalizing — talking out loud helps you make sense of what you feel.",
      acting: "Anger is the emotion most reliably linked to real-world civic action — attending events, donating, contacting representatives. Channel it.",
    },
    research: "A 2023 study in Communication Research found anger-expressing tweets had deeper retweet chains and reached more people than anxiety-expressing tweets across all measures of virality.",
    tips: [
      "Before sharing, pause 60 seconds and ask: 'Is this verified? Am I spreading information or amplifying a feeling?'",
      "Redirect anger toward specific, actionable targets — organizations, representatives, campaigns",
      "Audit your feed: which sources are consistently making you angry? Are they informing you or activating you?",
      "Find one solutions story for every three outrage stories you consume",
    ],
  },
  "Awe/Hope": {
    title: "The Connective Amplifier",
    emoji: "✨",
    color: "#5B8FD4",
    description:
      "Awe and hope are your dominant emotional orientation to news. You are drawn to stories of human achievement, connection, and possibility — and you spread them. Research shows awe is one of the most shareable emotions and produces prosocial effects, including increased willingness to help others and engage in collective action.",
    strengths: ["Natural positivity amplifier in your network", "Less susceptible to outrage loops and doomscrolling", "Awe has been shown to increase civic engagement and community investment"],
    watchFor: ["Underexposure to genuinely important but difficult news", "Algorithms may be learning to serve you feel-good content at the expense of informative content", "Toxic positivity — avoiding difficult news can create blind spots"],
    habits: {
      scrolling: "Your scrolling is likely more pleasurable than anxious. You may gravitate toward certain uplifting newsletters, accounts, or communities that feel like a refuge from 'the doom.'",
      sharing: "You share good news readily and enthusiastically. Your shares tend to be warmly received, which reinforces the behavior.",
      avoiding: "You likely avoid consistently negative, cynical, or violent content. This is emotionally healthy but worth auditing for informational completeness.",
      discussing: "You bring a connective, hopeful energy to news discussions. People may seek you out as a counterweight to more anxious voices in your circle.",
      acting: "Hope and awe are strong drivers of community-oriented action — volunteering, donating, civic participation. Less effective for urgent political mobilization.",
    },
    research: "Research on awe shows it produces a 'small self' effect — reducing ego focus and increasing prosocial behavior. High-arousal positive emotions like awe are among the most virally shared content.",
    tips: [
      "Deliberately include one serious, difficult news source in your regular diet to complement your hopeful orientation",
      "Use your awe-sharing tendency intentionally — solutions journalism and positive civic stories need amplification",
      "Notice if 'hope content' is sometimes a way of avoiding rather than balancing difficult news",
      "Your emotional openness makes you a good bridge-builder in polarized conversations",
    ],
  },
  Sadness: {
    title: "The Empathic Processor",
    emoji: "🌧️",
    color: "#7B9EA6",
    description:
      "Sadness is your primary emotional response to news. Unlike anxiety or anger, sadness tends to slow you down and deepen engagement rather than accelerate it. Research shows sadness can actually sustain information engagement rather than trigger avoidance — but it also carries a risk of emotional exhaustion and withdrawal.",
    strengths: ["Deep empathy makes you a thoughtful consumer of complex human stories", "Less likely to share reactively — you process before acting", "Sadness-driven engagement tends toward discussion and depth rather than virality"],
    watchFor: ["Compassion fatigue — sustained sadness without outlet becomes numbness or withdrawal", "Sadness can make news feel abstractly heavy rather than personally actionable", "The feeling of helplessness that often accompanies sadness can suppress civic action"],
    habits: {
      scrolling: "Your scrolling may follow emotional waves — periods of deep engagement with difficult content followed by withdrawal. You're more likely to read deeply than to skim.",
      sharing: "You share less reactively than anger or fear-driven consumers, but when you do share, it's thoughtful — you tend to add context or personal reflection.",
      avoiding: "You may avoid news during periods of personal difficulty, when emotional resources are already depleted.",
      discussing: "Discussion is your primary coping mechanism — talking through difficult news helps you process and find meaning. You're often the person others turn to for emotional grounding.",
      acting: "Sadness can motivate action when it's tied to a clear, human-scale story — but abstract or systemic tragedies can produce helplessness. Concrete, proximate stories move you more.",
    },
    research: "Studies show sadness, unlike high-arousal emotions, tends to slow information processing and increase depth of engagement. It can motivate seeking social support through discussion.",
    tips: [
      "Build deliberate emotional recovery practices into your news diet — physical activity, creativity, time in nature",
      "Look for 'what's being done about this' angles on sad stories to connect empathy to agency",
      "Your tendency to discuss is healthy — but notice if discussions deepen sadness without resolution",
      "Consider solutions journalism outlets (e.g., The Solutions Story Tracker) that pair problems with human-scale responses",
    ],
  },
  "Motivated Concern": {
    title: "The Informed Activist",
    emoji: "🧭",
    color: "#4CAF84",
    description:
      "You are among the most psychologically healthy news consumers — you feel concern without being overwhelmed, and you channel it toward action rather than reaction. Research on news engagement suggests this combination of emotional activation and self-regulatory capacity is rare and represents an ideal balance.",
    strengths: ["You turn information into agency rather than anxiety", "Less susceptible to algorithmic emotional manipulation", "Your news consumption is more likely to translate into civic impact"],
    watchFor: ["Risk of underestimating how emotionally affected you are — suppressed emotion can build up", "Others in your network may be more emotionally activated; bridge-building is a skill worth developing", "Concern without vulnerability can sometimes come across as detachment"],
    habits: {
      scrolling: "Your scrolling is likely more intentional and structured than most. You probably have routines around news consumption rather than reactive checking.",
      sharing: "You share strategically — not to vent, but to inform. Your shares tend to include context and calls to action.",
      avoiding: "You set boundaries on news consumption by design rather than avoidance — a meaningful distinction.",
      discussing: "You bring grounding and perspective to news discussions. You may sometimes frustrate others who want to process emotions before problem-solving.",
      acting: "You are the most likely of all profiles to translate news directly into real-world action. The challenge is sustaining this without burning out.",
    },
    research: "Research on information seeking and action suggests that concern paired with perceived efficacy — belief that action is possible — is the strongest predictor of civic engagement.",
    tips: [
      "Your strength is modeling intentional consumption for others — consider making your habits visible",
      "Build emotional release practices even if you don't feel 'overwhelmed' — it prevents long-term fatigue",
      "Your network likely needs your grounding presence; show up in conversations where panic or despair dominate",
      "Continue auditing your sources for quality over quantity",
    ],
  },
  Skepticism: {
    title: "The Critical Evaluator",
    emoji: "🔍",
    color: "#9B7ED6",
    description:
      "Skepticism is your dominant orientation — you approach news as something to be evaluated rather than felt or acted upon immediately. This is a genuine epistemic strength in an era of misinformation, but research shows it can also suppress civic engagement when distrust becomes a default rather than a tool.",
    strengths: ["Highly resistant to misinformation and emotional manipulation", "Lateral reading and source-checking come naturally to you", "You slow down the misinformation pipeline in your network"],
    watchFor: ["Skepticism can become cynicism — a blanket dismissal of all information", "Action is often delayed or suppressed because 'the full story isn't in yet'", "You may frustrate people in your network who want to act now based on imperfect information"],
    habits: {
      scrolling: "You likely scroll with a running internal commentary of 'but is this actually true?' — which is healthy but can also make news consumption exhausting.",
      sharing: "You share rarely and carefully, often adding caveats. Your shares are reliable but infrequent.",
      avoiding: "You may avoid highly emotional content not because it upsets you but because you distrust its framing.",
      discussing: "You add enormous value to group discussions but may be perceived as a wet blanket. Your instinct to complicate simple narratives is often correct.",
      acting: "Skepticism is one of the stronger suppressors of civic action — 'I need more information' can become a permanent deferral. Watch for this.",
    },
    research: "Research on inoculation theory suggests that skepticism functions as psychological immunity against manipulation — but studies also show it can reduce trust in legitimate sources and suppress prosocial behavior.",
    tips: [
      "Distinguish between verifying information (healthy) and indefinitely deferring action (avoidance)",
      "Identify two or three sources you genuinely trust and use them as an anchor — not as your only input",
      "Practice 'good enough' information consumption: not everything requires full verification before forming a view",
      "Your skeptical voice is needed in group settings — learn to deliver it in ways that open conversation rather than close it",
    ],
  },
  Overwhelm: {
    title: "The Overloaded Observer",
    emoji: "🌊",
    color: "#E87060",
    description:
      "Overwhelm is your dominant news experience. The volume and intensity of news has outpaced your capacity to process and respond to it — a phenomenon researchers call 'information anxiety.' This is one of the fastest-growing patterns in news consumption, particularly among heavy social media users.",
    strengths: ["Deep awareness of how much is happening — you're not in denial", "Overwhelm often indicates high empathy and genuine concern", "You're motivated to find a better relationship with news"],
    watchFor: ["Overwhelm is a strong predictor of information avoidance — and avoidance leaves you less informed, not more protected", "The sense that 'everything is terrible' may be algorithmically amplified rather than accurate", "Chronic overwhelm without intervention can lead to news fatigue and civic disengagement"],
    habits: {
      scrolling: "Your scrolling may feel compulsive rather than intentional — you open news apps without quite knowing why, or to check 'if anything happened.'",
      sharing: "You may share less than you feel you 'should' because you're not sure what to say or what difference it makes.",
      avoiding: "Avoidance is your most common coping strategy, but it tends to be episodic — you return, feel overwhelmed, avoid again.",
      discussing: "Discussions can increase overwhelm if they don't move toward resolution. You may withdraw from news conversations even when you want to be involved.",
      acting: "Overwhelm is one of the strongest suppressors of action — you have the emotion but not the clarity or energy to channel it.",
    },
    research: "Research published in PMC found that information overload creates stress, fatigue, and exhaustion, leading to discontinuation of information seeking and ultimately information avoidance.",
    tips: [
      "Dramatically reduce the number of news sources — depth over breadth is more sustainable",
      "Replace push notifications with pull — check news on your schedule, not when it calls for you",
      "For every story that overwhelms you, ask: 'What is one small thing I could do?' This rebuilds the connection between information and agency",
      "Consider a structured news diet: one quality daily briefing, one longer weekly read, and deliberate disconnection outside those windows",
    ],
  },
  Disgust: {
    title: "The Disengaged Realist",
    emoji: "😶",
    color: "#8B9467",
    description:
      "Disgust is your dominant news emotion — a powerful response that typically drives withdrawal rather than engagement. Unlike anger (which approaches), disgust pulls you away from what you find morally offensive or corrupt. This produces a pattern of selective avoidance that can protect your emotional wellbeing but limit your civic engagement.",
    strengths: ["Strong moral clarity — you know what violates your values", "Less likely to be emotionally manipulated by content that offends you", "Disgust can be a realistic response to genuinely dysfunctional systems"],
    watchFor: ["Disgust-driven avoidance can become a permanent civic disengagement strategy", "The 'they're all the same' or 'nothing matters' mindset often grows from unprocessed disgust", "Your news avoidance may be larger than you realize"],
    habits: {
      scrolling: "You likely scroll less than most profiles, or scroll very selectively. You've curated your feed to exclude what repels you — but this can become its own bubble.",
      sharing: "You share infrequently and often share content that expresses moral critique rather than information.",
      avoiding: "Avoidance is your dominant news behavior — not always a problem, but worth examining whether it's protective or a form of disengagement.",
      discussing: "You discuss news from a position of moral weariness — conversations can become nihilistic without careful framing.",
      acting: "Disgust rarely produces action — it produces withdrawal. The key is identifying whether any source of hope or efficacy can cut through the disillusionment.",
    },
    research: "Research on discrete emotions and information behavior shows disgust reliably suppresses engagement and sharing, in contrast to anger which drives approach behaviors.",
    tips: [
      "Explore local or solutions-focused journalism — smaller stakes, more visible impact, less systemic disgust",
      "Find one person or organization doing work you respect and follow their story closely as an antidote to cynicism",
      "Distinguish between protective disengagement (choosing your news inputs carefully) and civic withdrawal (checking out entirely)",
      "Your moral clarity is a resource — find communities or causes that give it a constructive outlet",
    ],
  },
  Disconnection: {
    title: "The Detached Observer",
    emoji: "🪟",
    color: "#A0A0A0",
    description:
      "Your news relationship is characterized by emotional disconnection — you consume news without strong emotional reactions, and rarely feel moved to share, discuss, or act on what you read. This may be a deliberate protective strategy, a response to past burnout, or a longer pattern of civic disengagement.",
    strengths: ["Emotional stability — you're not easily manipulated by sensational content", "Can engage with difficult content without being destabilized", "Lower susceptibility to outrage spirals and doomscrolling"],
    watchFor: ["Disconnection can mean you're absorbing more emotional content than you realize, but suppressing the response", "Civic participation tends to drop significantly when emotional connection to news is absent", "Detachment may be masking fatigue or burnout rather than genuine equanimity"],
    habits: {
      scrolling: "You scroll out of habit more than urgency — news is something you do, not something you feel.",
      sharing: "You share rarely, and when you do, it feels somewhat effortful or obligatory.",
      avoiding: "You don't dramatically avoid news, but your engagement is shallow — you skim rather than absorb.",
      discussing: "News discussions feel flat to you, or you find yourself playing devil's advocate without having a real stake.",
      acting: "Action feels disconnected from news consumption — you may act on other motivations (personal experience, relationships) but not on what you read.",
    },
    research: "Studies on civic disengagement suggest emotional connection to news is a prerequisite for many forms of political and social participation — not sufficient alone, but necessary.",
    tips: [
      "Try reading one long-form narrative piece per week — personal stories in news create emotional connection better than headlines",
      "Find your entry point: what topics make you feel something? Start there and build outward",
      "Consider whether detachment is protective or has become a habit that no longer serves you",
      "Connecting with others who care about an issue can 'turn the emotion back on' more effectively than solo news consumption",
    ],
  },
};


const DEFAULT_PROFILE = PROFILES["Motivated Concern"];

// ─── Color tokens (coordinated with thenewsfix.org) ─────────────────────────
// Background: warm cream | Text: deep forest green | Accent: moss green
// Cards: white with soft shadow | Distinct from Newsfix: more structured/editorial

const quizStyles = {
  root: {
    minHeight: "100vh",
    background: "#EDEEE6",
    color: "#2C3E2C",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    position: "relative",
  },
  grain: {
    display: "none", // not needed on light background
  },

  // INTRO
  introWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    padding: "2rem",
  },
  introCard: {
    maxWidth: 600,
    width: "100%",
    background: "#FFFFFF",
    borderRadius: 12,
    padding: "3rem 2.75rem",
    boxShadow: "0 2px 20px rgba(44,62,44,0.08)",
    border: "1px solid rgba(44,62,44,0.08)",
  },
  badge: {
    display: "inline-block",
    fontSize: 10,
    letterSpacing: "0.18em",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#5A7A5A",
    border: "1px solid #B8CDB8",
    borderRadius: 20,
    padding: "3px 12px",
    marginBottom: "1.5rem",
    background: "#F2F7F2",
  },
  introTitle: {
    fontSize: "clamp(1.9rem, 5vw, 2.75rem)",
    fontWeight: 400,
    lineHeight: 1.18,
    margin: "0 0 1.25rem",
    letterSpacing: "-0.02em",
    color: "#1E3320",
  },
  titleAccent: {
    fontStyle: "italic",
    color: "#3D6B3D",
  },
  introBody: {
    fontSize: "1rem",
    lineHeight: 1.8,
    color: "#4A5E4A",
    marginBottom: "1.75rem",
  },
  pillRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: "2rem",
  },
  pill: {
    fontSize: 11,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#5A7A5A",
    background: "#F2F7F2",
    border: "1px solid #C8D8C8",
    borderRadius: 20,
    padding: "3px 12px",
  },
  startBtn: {
    display: "block",
    width: "100%",
    background: "#2D4A2D",
    color: "#FFFFFF",
    border: "none",
    borderRadius: 8,
    padding: "1rem 2rem",
    fontSize: "1rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    fontWeight: 400,
    cursor: "pointer",
    letterSpacing: "0.01em",
    marginBottom: "1.5rem",
    transition: "background 0.2s",
  },
  introNote: {
    fontSize: 11,
    color: "#8A9E8A",
    lineHeight: 1.6,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    textAlign: "center",
  },

  // QUIZ
  quizWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minHeight: "100vh",
    padding: "0 1rem 4rem",
    background: "#EDEEE6",
  },
  progressBar: {
    width: "100%",
    height: 3,
    background: "#D4DDD4",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #3D6B3D, #5A8A5A)",
    transition: "width 0.4s ease",
  },
  quizCard: {
    maxWidth: 620,
    width: "100%",
    marginTop: "3rem",
    padding: "0 0.5rem",
  },
  sectionLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#6A8A6A",
    letterSpacing: "0.04em",
    marginBottom: "2rem",
  },
  sectionDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#3D6B3D",
    flexShrink: 0,
  },
  questionBlock: {},
  qCounter: {
    fontSize: 12,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#8A9E8A",
    marginBottom: "0.75rem",
  },
  qText: {
    fontSize: "clamp(1.1rem, 3vw, 1.3rem)",
    fontWeight: 400,
    lineHeight: 1.6,
    marginBottom: "2rem",
    color: "#1E3320",
  },
  optionsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: "2.5rem",
  },
  optionBtn: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    textAlign: "left",
    background: "#FFFFFF",
    border: "1px solid #D4DDD4",
    borderRadius: 8,
    padding: "0.9rem 1.1rem",
    color: "#3A4E3A",
    fontSize: "0.92rem",
    fontFamily: "'Georgia', serif",
    lineHeight: 1.55,
    cursor: "pointer",
    transition: "all 0.15s",
    boxShadow: "0 1px 3px rgba(44,62,44,0.04)",
  },
  optionSelected: {
    background: "#F0F6F0",
    border: "1px solid #3D6B3D",
    color: "#1E3320",
  },
  optionCheck: {
    color: "#3D6B3D",
    fontSize: "1rem",
    flexShrink: 0,
    marginTop: 1,
  },
  navRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    alignItems: "center",
  },
  backBtn: {
    background: "transparent",
    border: "1px solid #C8D8C8",
    color: "#6A8A6A",
    borderRadius: 6,
    padding: "0.65rem 1.25rem",
    fontSize: "0.85rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    cursor: "pointer",
  },
  nextBtn: {
    background: "#2D4A2D",
    color: "#FFFFFF",
    border: "none",
    borderRadius: 8,
    padding: "0.75rem 1.75rem",
    fontSize: "0.92rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  nextDisabled: {
    opacity: 0.35,
    cursor: "not-allowed",
  },

  // RESULTS
  resultsWrap: {
    maxWidth: 680,
    margin: "0 auto",
    padding: "2.5rem 1.25rem 5rem",
  },
  resultsHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "1.5rem",
    borderLeft: "3px solid",
    paddingLeft: "1.5rem",
    marginBottom: "2rem",
  },
  profileEmoji: {
    fontSize: "2.5rem",
    lineHeight: 1,
  },
  profileType: {
    fontSize: 11,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    letterSpacing: "0.06em",
    marginBottom: "0.4rem",
    color: "#5A7A5A",
  },
  profileTitle: {
    fontSize: "clamp(1.5rem, 4vw, 2.2rem)",
    fontWeight: 400,
    margin: "0 0 0.5rem",
    letterSpacing: "-0.02em",
    color: "#1E3320",
  },
  dominantTag: {
    fontSize: "0.85rem",
    color: "#6A8A6A",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
  },
  tabBar: {
    display: "flex",
    borderBottom: "2px solid #D4DDD4",
    marginBottom: "2rem",
    gap: 0,
  },
  tab: {
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    color: "#8A9E8A",
    padding: "0.75rem 1.25rem",
    fontSize: "0.88rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    cursor: "pointer",
    transition: "all 0.15s",
    marginBottom: -2,
  },
  tabActive: {
    borderBottom: "2px solid",
    color: "#2D4A2D",
  },
  tabContent: {},
  profileDesc: {
    fontSize: "1rem",
    lineHeight: 1.8,
    color: "#3A4E3A",
    marginBottom: "2rem",
    borderLeft: "3px solid #C8D8C8",
    paddingLeft: "1.25rem",
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: "1.5rem",
  },
  infoBox: {
    background: "#FFFFFF",
    border: "1px solid #D4DDD4",
    borderRadius: 8,
    padding: "1.25rem",
    boxShadow: "0 1px 4px rgba(44,62,44,0.05)",
  },
  boxTitle: {
    fontSize: "0.78rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#6A8A6A",
    marginBottom: "0.75rem",
    fontWeight: 400,
  },
  listItem: {
    fontSize: "0.88rem",
    lineHeight: 1.65,
    color: "#3A4E3A",
    marginBottom: "0.5rem",
    display: "flex",
    alignItems: "flex-start",
  },
  researchBox: {
    background: "#F2F7F2",
    border: "1px solid #C8D8C8",
    borderRadius: 8,
    padding: "1.25rem",
    marginTop: "1.5rem",
  },
  researchLabel: {
    fontSize: 11,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#5A7A5A",
    display: "block",
    marginBottom: "0.5rem",
  },
  researchText: {
    fontSize: "0.88rem",
    lineHeight: 1.7,
    color: "#4A5E4A",
    margin: 0,
  },
  behaviorIntro: {
    fontSize: "0.95rem",
    lineHeight: 1.75,
    color: "#4A5E4A",
    marginBottom: "2rem",
  },
  chartArea: {
    background: "#FFFFFF",
    border: "1px solid #D4DDD4",
    borderRadius: 8,
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 1px 4px rgba(44,62,44,0.05)",
  },
  chartTitle: {
    fontSize: "0.9rem",
    fontWeight: 400,
    marginBottom: "0.25rem",
    fontFamily: "'Georgia', serif",
    color: "#1E3320",
  },
  chartSub: {
    fontSize: "0.78rem",
    color: "#8A9E8A",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    marginBottom: "1.25rem",
  },
  barRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: "0.75rem",
  },
  barLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    width: 90,
    fontSize: "0.82rem",
    color: "#6A8A6A",
    flexShrink: 0,
  },
  barTrack: {
    flex: 1,
    height: 7,
    background: "#E8EEE8",
    borderRadius: 4,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: 4,
    transition: "width 0.8s ease",
  },
  barVal: {
    width: 16,
    fontSize: "0.78rem",
    color: "#8A9E8A",
    fontFamily: "'Georgia', serif",
    textAlign: "right",
  },
  habitCard: {
    borderBottom: "1px solid #DDE6DD",
    paddingBottom: "1.25rem",
    marginBottom: "1.25rem",
  },
  habitHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: "0.5rem",
  },
  habitIcon: {
    fontSize: "1.1rem",
  },
  habitTitle: {
    fontSize: "0.85rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#3D6B3D",
    fontWeight: 400,
  },
  habitDesc: {
    fontSize: "0.92rem",
    lineHeight: 1.75,
    color: "#3A4E3A",
    margin: 0,
  },
  tipsIntro: {
    fontSize: "0.95rem",
    lineHeight: 1.75,
    color: "#4A5E4A",
    marginBottom: "1.75rem",
  },
  tipCard: {
    display: "flex",
    gap: 16,
    alignItems: "flex-start",
    marginBottom: "1.25rem",
    background: "#FFFFFF",
    border: "1px solid #D4DDD4",
    borderRadius: 8,
    padding: "1.1rem",
    boxShadow: "0 1px 3px rgba(44,62,44,0.04)",
  },
  tipNum: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.78rem",
    fontFamily: "'Georgia', serif",
    color: "#FFFFFF",
    fontWeight: 700,
    flexShrink: 0,
  },
  tipText: {
    fontSize: "0.92rem",
    lineHeight: 1.75,
    color: "#2C3E2C",
    margin: 0,
  },
  resourceBox: {
    background: "#F2F7F2",
    border: "1px solid #C8D8C8",
    borderRadius: 8,
    padding: "1.5rem",
    marginTop: "2rem",
  },
  resourceTitle: {
    fontSize: "0.78rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#6A8A6A",
    fontWeight: 400,
    marginBottom: "1.25rem",
  },
  resourceItem: {
    marginBottom: "1rem",
    paddingBottom: "1rem",
    borderBottom: "1px solid #D4DDD4",
  },
  resourceName: {
    fontSize: "0.9rem",
    color: "#2D4A2D",
    display: "block",
    marginBottom: "0.25rem",
    fontFamily: "'Georgia', serif",
    fontWeight: 400,
    fontStyle: "italic",
  },
  resourceDesc: {
    fontSize: "0.82rem",
    color: "#6A8A6A",
    lineHeight: 1.6,
    margin: 0,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
  },
  restartBtn: {
    display: "block",
    margin: "2.5rem auto 0",
    background: "transparent",
    border: "1px solid #C8D8C8",
    color: "#6A8A6A",
    borderRadius: 6,
    padding: "0.65rem 1.5rem",
    fontSize: "0.85rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    cursor: "pointer",
  },
};


function EmotionQuizTab() {
  const [screen, setScreen] = useState("intro"); // intro | quiz | results
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [animating, setAnimating] = useState(false);

  const progress = (currentQ / QUESTIONS.length) * 100;
  const q = QUESTIONS[currentQ];

  function handleSelect(value) {
    setSelected(value);
  }

  function handleNext() {
    if (!selected) return;
    const newAnswers = { ...answers, [q.id]: selected };
    setAnswers(newAnswers);

    if (currentQ < QUESTIONS.length - 1) {
      setAnimating(true);
      setTimeout(() => {
        setCurrentQ(currentQ + 1);
        setSelected(null);
        setAnimating(false);
      }, 280);
    } else {
      const scored = scoreAnswers(newAnswers);
      const profile = PROFILES[scored.dominant] || DEFAULT_PROFILE;
      setResults({ ...scored, profile });
      setScreen("results");

    }
  }

  function restart() {
    setScreen("intro");
    setCurrentQ(0);
    setAnswers({});
    setSelected(null);
    setResults(null);
    setActiveTab("profile");
  }

  const totalBehavior = results
    ? results.seekCount + results.shareCount + results.avoidCount + results.discussCount + results.actCount
    : 1;

  const behaviorBars = results
    ? [
        { label: "Seeking", count: results.seekCount, color: "#E8A838", icon: "🔍" },
        { label: "Sharing", count: results.shareCount, color: "#D94F3D", icon: "📤" },
        { label: "Avoiding", count: results.avoidCount, color: "#7B9EA6", icon: "🙈" },
        { label: "Discussing", count: results.discussCount, color: "#5B8FD4", icon: "💬" },
        { label: "Acting", count: results.actCount, color: "#4CAF84", icon: "⚡" },
      ]
    : [];

  return (
    <div style={quizStyles.root}>
      {screen === "intro" && (
        <div style={quizStyles.introWrap}>
          <div style={quizStyles.introCard}>
            <div style={quizStyles.badge}>EMOTION × NEWS</div>
            <h1 style={quizStyles.introTitle}>
              Which feelings are driving<br />
              <span style={quizStyles.titleAccent}>your information diet?</span>
            </h1>
            <p style={quizStyles.introBody}>
              Research shows that different emotions like fear, anger, awe, and sadness have a big impact on
              the information we seek, share, avoid, discuss, and act on. This quiz maps an emotional profile
              to informational behaviors. You can better understand your own patterns and be more in control
              of your information diet and your feelings.
            </p>
            <div style={quizStyles.pillRow}>
              {["12 questions", "~4 minutes", "Based on peer-reviewed research"].map((t) => (
                <span key={t} style={quizStyles.pill}>{t}</span>
              ))}
            </div>
            <button style={quizStyles.startBtn} onClick={() => setScreen("quiz")}>
              Begin Assessment →
            </button>
            <p style={quizStyles.introNote}>
              Developed by Sarah Alvarez with help from Claude.ai
            </p>
          </div>
        </div>
      )}

      {screen === "quiz" && q && (
        <div style={quizStyles.quizWrap}>
          {/* Progress */}
          <div style={quizStyles.progressBar}>
            <div style={{ ...quizStyles.progressFill, width: `${progress}%` }} />
          </div>

          <div style={quizStyles.quizCard}>
            <div style={quizStyles.sectionLabel}>
              <span style={quizStyles.sectionDot} />
              {q.section} — {q.sectionDesc}
            </div>

            <div style={{ ...quizStyles.questionBlock, opacity: animating ? 0 : 1, transition: "opacity 0.25s" }}>
              <p style={quizStyles.qCounter}>
                Question {currentQ + 1} of {QUESTIONS.length}
              </p>
              <h2 style={quizStyles.qText}>{q.text}</h2>

              <div style={quizStyles.optionsGrid}>
                {q.options.map((opt) => (
                  <button
                    key={opt.value}
                    style={{
                      ...quizStyles.optionBtn,
                      ...(selected === opt.value ? quizStyles.optionSelected : {}),
                    }}
                    onClick={() => handleSelect(opt.value)}
                  >
                    <span style={quizStyles.optionCheck}>
                      {selected === opt.value ? "●" : "○"}
                    </span>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={quizStyles.navRow}>
              {currentQ > 0 && (
                <button
                  style={quizStyles.backBtn}
                  onClick={() => {
                    setCurrentQ(currentQ - 1);
                    setSelected(answers[QUESTIONS[currentQ - 1].id] || null);
                  }}
                >
                  ← Back
                </button>
              )}
              <button
                style={{ ...quizStyles.nextBtn, ...(selected ? {} : quizStyles.nextDisabled) }}
                onClick={handleNext}
                disabled={!selected}
              >
                {currentQ === QUESTIONS.length - 1 ? "See My Results →" : "Next →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {screen === "results" && results && (
        <div style={quizStyles.resultsWrap}>
          {/* Header */}
          <div style={{ ...quizStyles.resultsHeader, borderColor: results.profile.color }}>
            <div style={quizStyles.profileEmoji}>{results.profile.emoji}</div>
            <div>
              <div style={{ ...quizStyles.profileType, color: results.profile.color }}>
                Your Emotional Profile
              </div>
              <h1 style={quizStyles.profileTitle}>{results.profile.title}</h1>
              <div style={quizStyles.dominantTag}>
                Dominant emotion: <strong>{results.dominant}</strong>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={quizStyles.tabBar}>
            {[
              { id: "profile", label: "Profile" },
              { id: "behaviors", label: "News Behaviors" },
              { id: "tips", label: "Growth Tips" },
            ].map((tab) => (
              <button
                key={tab.id}
                style={{
                  ...quizStyles.tab,
                  ...(activeTab === tab.id ? { ...quizStyles.tabActive, borderColor: results.profile.color, color: results.profile.color } : {}),
                }}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div style={quizStyles.tabContent}>
            {activeTab === "profile" && (
              <div>
                <p style={quizStyles.profileDesc}>{results.profile.description}</p>

                <div style={quizStyles.twoCol}>
                  <div style={quizStyles.infoBox}>
                    <h3 style={quizStyles.boxTitle}>✓ Your Strengths</h3>
                    {results.profile.strengths.map((s) => (
                      <div key={s} style={quizStyles.listItem}>
                        <span style={{ color: "#4CAF84", marginRight: 8 }}>▸</span>{s}
                      </div>
                    ))}
                  </div>
                  <div style={quizStyles.infoBox}>
                    <h3 style={quizStyles.boxTitle}>⚠ Watch For</h3>
                    {results.profile.watchFor.map((s) => (
                      <div key={s} style={quizStyles.listItem}>
                        <span style={{ color: results.profile.color, marginRight: 8 }}>▸</span>{s}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={quizStyles.researchBox}>
                  <span style={quizStyles.researchLabel}>📚 Research basis</span>
                  <p style={quizStyles.researchText}>{results.profile.research}</p>
                </div>
              </div>
            )}

            {activeTab === "behaviors" && (
              <div>
                <p style={quizStyles.behaviorIntro}>
                  Based on your answers, here's how your emotional profile likely shows up across five key
                  news behaviors — and what the research says about each.
                </p>

                {/* Bar chart */}
                <div style={quizStyles.chartArea}>
                  <h3 style={quizStyles.chartTitle}>Your Behavioral Tendencies</h3>
                  <p style={quizStyles.chartSub}>Relative strength of each behavior signal in your responses</p>
                  {behaviorBars.map((b) => (
                    <div key={b.label} style={quizStyles.barRow}>
                      <div style={quizStyles.barLabel}>
                        <span>{b.icon}</span>
                        <span>{b.label}</span>
                      </div>
                      <div style={quizStyles.barTrack}>
                        <div
                          style={{
                            ...quizStyles.barFill,
                            width: `${Math.max(4, (b.count / (totalBehavior || 1)) * 100)}%`,
                            background: b.color,
                          }}
                        />
                      </div>
                      <span style={quizStyles.barVal}>{b.count}</span>
                    </div>
                  ))}
                </div>

                {/* Per-behavior detail */}
                {Object.entries(results.profile.habits).map(([key, desc]) => {
                  const labels = {
                    scrolling: { icon: "📱", title: "Scrolling" },
                    sharing: { icon: "📤", title: "Sharing" },
                    avoiding: { icon: "🙈", title: "Avoiding" },
                    discussing: { icon: "💬", title: "Discussing" },
                    acting: { icon: "⚡", title: "Acting" },
                  };
                  const l = labels[key];
                  return (
                    <div key={key} style={quizStyles.habitCard}>
                      <div style={quizStyles.habitHeader}>
                        <span style={quizStyles.habitIcon}>{l.icon}</span>
                        <strong style={quizStyles.habitTitle}>{l.title}</strong>
                      </div>
                      <p style={quizStyles.habitDesc}>{desc}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {activeTab === "tips" && (
              <div>
                <p style={quizStyles.tipsIntro}>
                  These recommendations are drawn from research on emotion regulation, media literacy, and
                  information behavior — specifically calibrated to your profile.
                </p>
                {results.profile.tips.map((tip, i) => (
                  <div key={i} style={quizStyles.tipCard}>
                    <div style={{ ...quizStyles.tipNum, background: results.profile.color }}>{i + 1}</div>
                    <p style={quizStyles.tipText}>{tip}</p>
                  </div>
                ))}

                <div style={quizStyles.resourceBox}>
                  <h3 style={quizStyles.resourceTitle}>Recommended Tools & Further Reading</h3>
                  {[
                    { name: "Paul Ekman Emotional Triggers Quiz", url: "https://www.paulekman.com/quizzes/emotional-triggers-quiz/", desc: "Identify your strongest emotional triggers across 7 universal emotions" },
                    { name: "Atlas of Emotions", url: "https://atlasofemotions.org", desc: "Interactive map of emotional states, triggers, and responses — built by Drs. Paul & Eve Ekman for the Dalai Lama" },
                    { name: "Bad News Game (Cambridge)", url: "https://www.getbadnews.com", desc: "Play-through inoculation against emotional manipulation tactics in news" },
                    { name: "AllSides Media Bias Ratings", url: "https://www.allsides.com/media-bias/ratings", desc: "Audit your regular news sources for political lean" },
                    { name: "Solutions Journalism Network", url: "https://www.solutionsjournalism.org", desc: "Find rigorous reporting on responses to problems — counterweight to negativity bias" },
                  ].map((r) => (
                    <div key={r.name} style={quizStyles.resourceItem}>
                      <strong style={quizStyles.resourceName}>{r.name}</strong>
                      <p style={quizStyles.resourceDesc}>{r.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button style={quizStyles.restartBtn} onClick={restart}>
            ↺ Retake Assessment
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

// ─── Color tokens (coordinated with thenewsfix.org) ─────────────────────────
// Background: warm cream | Text: deep forest green | Accent: moss green
// Cards: white with soft shadow | Distinct from Newsfix: more structured/editorial

// eslint-disable-next-line no-unused-vars
const styles = {
  root: {
    minHeight: "100vh",
    background: "#EDEEE6",
    color: "#2C3E2C",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    position: "relative",
  },
  grain: {
    display: "none", // not needed on light background
  },

  // INTRO
  introWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    padding: "2rem",
  },
  introCard: {
    maxWidth: 600,
    width: "100%",
    background: "#FFFFFF",
    borderRadius: 12,
    padding: "3rem 2.75rem",
    boxShadow: "0 2px 20px rgba(44,62,44,0.08)",
    border: "1px solid rgba(44,62,44,0.08)",
  },
  badge: {
    display: "inline-block",
    fontSize: 10,
    letterSpacing: "0.18em",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#5A7A5A",
    border: "1px solid #B8CDB8",
    borderRadius: 20,
    padding: "3px 12px",
    marginBottom: "1.5rem",
    background: "#F2F7F2",
  },
  introTitle: {
    fontSize: "clamp(1.9rem, 5vw, 2.75rem)",
    fontWeight: 400,
    lineHeight: 1.18,
    margin: "0 0 1.25rem",
    letterSpacing: "-0.02em",
    color: "#1E3320",
  },
  titleAccent: {
    fontStyle: "italic",
    color: "#3D6B3D",
  },
  introBody: {
    fontSize: "1rem",
    lineHeight: 1.8,
    color: "#4A5E4A",
    marginBottom: "1.75rem",
  },
  pillRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: "2rem",
  },
  pill: {
    fontSize: 11,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#5A7A5A",
    background: "#F2F7F2",
    border: "1px solid #C8D8C8",
    borderRadius: 20,
    padding: "3px 12px",
  },
  startBtn: {
    display: "block",
    width: "100%",
    background: "#2D4A2D",
    color: "#FFFFFF",
    border: "none",
    borderRadius: 8,
    padding: "1rem 2rem",
    fontSize: "1rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    fontWeight: 400,
    cursor: "pointer",
    letterSpacing: "0.01em",
    marginBottom: "1.5rem",
    transition: "background 0.2s",
  },
  introNote: {
    fontSize: 11,
    color: "#8A9E8A",
    lineHeight: 1.6,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    textAlign: "center",
  },

  // QUIZ
  quizWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minHeight: "100vh",
    padding: "0 1rem 4rem",
    background: "#EDEEE6",
  },
  progressBar: {
    width: "100%",
    height: 3,
    background: "#D4DDD4",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  progressFill: {
    height: "100%",
    background: "linear-gradient(90deg, #3D6B3D, #5A8A5A)",
    transition: "width 0.4s ease",
  },
  quizCard: {
    maxWidth: 620,
    width: "100%",
    marginTop: "3rem",
    padding: "0 0.5rem",
  },
  sectionLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#6A8A6A",
    letterSpacing: "0.04em",
    marginBottom: "2rem",
  },
  sectionDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#3D6B3D",
    flexShrink: 0,
  },
  questionBlock: {},
  qCounter: {
    fontSize: 12,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#8A9E8A",
    marginBottom: "0.75rem",
  },
  qText: {
    fontSize: "clamp(1.1rem, 3vw, 1.3rem)",
    fontWeight: 400,
    lineHeight: 1.6,
    marginBottom: "2rem",
    color: "#1E3320",
  },
  optionsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginBottom: "2.5rem",
  },
  optionBtn: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    textAlign: "left",
    background: "#FFFFFF",
    border: "1px solid #D4DDD4",
    borderRadius: 8,
    padding: "0.9rem 1.1rem",
    color: "#3A4E3A",
    fontSize: "0.92rem",
    fontFamily: "'Georgia', serif",
    lineHeight: 1.55,
    cursor: "pointer",
    transition: "all 0.15s",
    boxShadow: "0 1px 3px rgba(44,62,44,0.04)",
  },
  optionSelected: {
    background: "#F0F6F0",
    border: "1px solid #3D6B3D",
    color: "#1E3320",
  },
  optionCheck: {
    color: "#3D6B3D",
    fontSize: "1rem",
    flexShrink: 0,
    marginTop: 1,
  },
  navRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    alignItems: "center",
  },
  backBtn: {
    background: "transparent",
    border: "1px solid #C8D8C8",
    color: "#6A8A6A",
    borderRadius: 6,
    padding: "0.65rem 1.25rem",
    fontSize: "0.85rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    cursor: "pointer",
  },
  nextBtn: {
    background: "#2D4A2D",
    color: "#FFFFFF",
    border: "none",
    borderRadius: 8,
    padding: "0.75rem 1.75rem",
    fontSize: "0.92rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  nextDisabled: {
    opacity: 0.35,
    cursor: "not-allowed",
  },

  // RESULTS
  resultsWrap: {
    maxWidth: 680,
    margin: "0 auto",
    padding: "2.5rem 1.25rem 5rem",
  },
  resultsHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "1.5rem",
    borderLeft: "3px solid",
    paddingLeft: "1.5rem",
    marginBottom: "2rem",
  },
  profileEmoji: {
    fontSize: "2.5rem",
    lineHeight: 1,
  },
  profileType: {
    fontSize: 11,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    letterSpacing: "0.06em",
    marginBottom: "0.4rem",
    color: "#5A7A5A",
  },
  profileTitle: {
    fontSize: "clamp(1.5rem, 4vw, 2.2rem)",
    fontWeight: 400,
    margin: "0 0 0.5rem",
    letterSpacing: "-0.02em",
    color: "#1E3320",
  },
  dominantTag: {
    fontSize: "0.85rem",
    color: "#6A8A6A",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
  },
  tabBar: {
    display: "flex",
    borderBottom: "2px solid #D4DDD4",
    marginBottom: "2rem",
    gap: 0,
  },
  tab: {
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    color: "#8A9E8A",
    padding: "0.75rem 1.25rem",
    fontSize: "0.88rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    cursor: "pointer",
    transition: "all 0.15s",
    marginBottom: -2,
  },
  tabActive: {
    borderBottom: "2px solid",
    color: "#2D4A2D",
  },
  tabContent: {},
  profileDesc: {
    fontSize: "1rem",
    lineHeight: 1.8,
    color: "#3A4E3A",
    marginBottom: "2rem",
    borderLeft: "3px solid #C8D8C8",
    paddingLeft: "1.25rem",
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: "1.5rem",
  },
  infoBox: {
    background: "#FFFFFF",
    border: "1px solid #D4DDD4",
    borderRadius: 8,
    padding: "1.25rem",
    boxShadow: "0 1px 4px rgba(44,62,44,0.05)",
  },
  boxTitle: {
    fontSize: "0.78rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#6A8A6A",
    marginBottom: "0.75rem",
    fontWeight: 400,
  },
  listItem: {
    fontSize: "0.88rem",
    lineHeight: 1.65,
    color: "#3A4E3A",
    marginBottom: "0.5rem",
    display: "flex",
    alignItems: "flex-start",
  },
  researchBox: {
    background: "#F2F7F2",
    border: "1px solid #C8D8C8",
    borderRadius: 8,
    padding: "1.25rem",
    marginTop: "1.5rem",
  },
  researchLabel: {
    fontSize: 11,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#5A7A5A",
    display: "block",
    marginBottom: "0.5rem",
  },
  researchText: {
    fontSize: "0.88rem",
    lineHeight: 1.7,
    color: "#4A5E4A",
    margin: 0,
  },
  behaviorIntro: {
    fontSize: "0.95rem",
    lineHeight: 1.75,
    color: "#4A5E4A",
    marginBottom: "2rem",
  },
  chartArea: {
    background: "#FFFFFF",
    border: "1px solid #D4DDD4",
    borderRadius: 8,
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 1px 4px rgba(44,62,44,0.05)",
  },
  chartTitle: {
    fontSize: "0.9rem",
    fontWeight: 400,
    marginBottom: "0.25rem",
    fontFamily: "'Georgia', serif",
    color: "#1E3320",
  },
  chartSub: {
    fontSize: "0.78rem",
    color: "#8A9E8A",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    marginBottom: "1.25rem",
  },
  barRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: "0.75rem",
  },
  barLabel: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    width: 90,
    fontSize: "0.82rem",
    color: "#6A8A6A",
    flexShrink: 0,
  },
  barTrack: {
    flex: 1,
    height: 7,
    background: "#E8EEE8",
    borderRadius: 4,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: 4,
    transition: "width 0.8s ease",
  },
  barVal: {
    width: 16,
    fontSize: "0.78rem",
    color: "#8A9E8A",
    fontFamily: "'Georgia', serif",
    textAlign: "right",
  },
  habitCard: {
    borderBottom: "1px solid #DDE6DD",
    paddingBottom: "1.25rem",
    marginBottom: "1.25rem",
  },
  habitHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: "0.5rem",
  },
  habitIcon: {
    fontSize: "1.1rem",
  },
  habitTitle: {
    fontSize: "0.85rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#3D6B3D",
    fontWeight: 400,
  },
  habitDesc: {
    fontSize: "0.92rem",
    lineHeight: 1.75,
    color: "#3A4E3A",
    margin: 0,
  },
  tipsIntro: {
    fontSize: "0.95rem",
    lineHeight: 1.75,
    color: "#4A5E4A",
    marginBottom: "1.75rem",
  },
  tipCard: {
    display: "flex",
    gap: 16,
    alignItems: "flex-start",
    marginBottom: "1.25rem",
    background: "#FFFFFF",
    border: "1px solid #D4DDD4",
    borderRadius: 8,
    padding: "1.1rem",
    boxShadow: "0 1px 3px rgba(44,62,44,0.04)",
  },
  tipNum: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.78rem",
    fontFamily: "'Georgia', serif",
    color: "#FFFFFF",
    fontWeight: 700,
    flexShrink: 0,
  },
  tipText: {
    fontSize: "0.92rem",
    lineHeight: 1.75,
    color: "#2C3E2C",
    margin: 0,
  },
  resourceBox: {
    background: "#F2F7F2",
    border: "1px solid #C8D8C8",
    borderRadius: 8,
    padding: "1.5rem",
    marginTop: "2rem",
  },
  resourceTitle: {
    fontSize: "0.78rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    color: "#6A8A6A",
    fontWeight: 400,
    marginBottom: "1.25rem",
  },
  resourceItem: {
    marginBottom: "1rem",
    paddingBottom: "1rem",
    borderBottom: "1px solid #D4DDD4",
  },
  resourceName: {
    fontSize: "0.9rem",
    color: "#2D4A2D",
    display: "block",
    marginBottom: "0.25rem",
    fontFamily: "'Georgia', serif",
    fontWeight: 400,
    fontStyle: "italic",
  },
  resourceDesc: {
    fontSize: "0.82rem",
    color: "#6A8A6A",
    lineHeight: 1.6,
    margin: 0,
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
  },
  restartBtn: {
    display: "block",
    margin: "2.5rem auto 0",
    background: "transparent",
    border: "1px solid #C8D8C8",
    color: "#6A8A6A",
    borderRadius: 6,
    padding: "0.65rem 1.5rem",
    fontSize: "0.85rem",
    fontFamily: "'Georgia', serif",
    fontStyle: "italic",
    cursor: "pointer",
  },
}

// ─── Main app ─────────────────────────────────────────────────────────────────

export default function InformationDiary() {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('diary');
  const [summaryDate, setSummaryDate] = useState(today());

  useEffect(() => {
    try {
      const raw = localStorage.getItem('diary-entries');
      if (raw) setEntries(JSON.parse(raw));
    } catch (e) {}
    setLoading(false);
  }, []);

  function persist(newEntries) { localStorage.setItem('diary-entries', JSON.stringify(newEntries)); }
  function handleChange(field, value) { setForm(f => ({ ...f, [field]: value })); }

  function handleSave() {
    if (!form.need && !form.source && !form.day) { alert('Please fill in at least a few fields.'); return; }
    let updated;
    if (editingId !== null) {
      updated = entries.map(e => e.id === editingId ? { ...e, ...form } : e);
      setEditingId(null);
    } else {
      updated = [{ id: Date.now(), ...form }, ...entries];
    }
    setEntries(updated); persist(updated); setForm(emptyForm());
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  }

  function handleEdit(entry) {
    setForm({ need: entry.need, source: entry.source, day: entry.day, timeOn: entry.timeOn, timeOff: entry.timeOff, satisfaction: entry.satisfaction, notes: entry.notes });
    setEditingId(entry.id); setActiveTab('diary');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleDelete(id) {
    if (!window.confirm('Delete this entry?')) return;
    const updated = entries.filter(e => e.id !== id);
    setEntries(updated); persist(updated);
  }

  const filtered = entries.filter(e => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [e.need, e.source, e.notes, e.day].some(f => f && f.toLowerCase().includes(q));
  });

  const dayEntries = entries.filter(e => e.day === summaryDate);
  const totalMins = dayEntries.reduce((acc, e) => acc + calcDurationMins(e.timeOn, e.timeOff), 0);
  const avgSat = dayEntries.length ? (dayEntries.reduce((acc, e) => acc + e.satisfaction, 0) / dayEntries.length).toFixed(1) : null;
  const motivations = dayEntries.reduce((acc, e) => { if (e.need) { acc[e.need] = (acc[e.need] || 0) + 1; } return acc; }, {});
  const sources = [...new Set(dayEntries.map(e => e.source).filter(Boolean))];

  const tabStyle = (tab) => ({
    padding: '10px 24px', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer',
    fontFamily: 'Georgia, serif', fontSize: '0.95rem', fontWeight: activeTab === tab ? 'bold' : 'normal',
    background: activeTab === tab ? '#fff' : '#e8f0e8', color: activeTab === tab ? '#3e5c3e' : '#7a7a7a',
    borderBottom: activeTab === tab ? '2px solid #5c7a5c' : 'none', transition: 'all 0.15s'
  });

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', background: '#f5f3ef', minHeight: '100vh', padding: '24px 16px' }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>

        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h1 style={{ fontSize: '2rem', color: '#3e5c3e' }}>📓 Track your info habit</h1>
          <p style={{ color: '#7a7a7a', marginTop: 6, fontStyle: 'italic' }}>Discover what's driving you to scroll, read, listen and watch for 24 hours, a few days, or more. Then use these insights to take more control of your media consumption and craft habits that better serve you.</p>
          <p style={{ color: '#5a5a5a', marginTop: 16, fontSize: '0.9rem', lineHeight: '1.6', maxWidth: 620, margin: '16px auto 0', textAlign: 'left' }}>
            This is a free tool developed by Sarah Alvarez to help you take more control of your media habits. This site is not collecting any data about you or your media preferences.
          </p>
          <p style={{ color: '#5a5a5a', marginTop: 10, fontSize: '0.9rem', lineHeight: '1.6', maxWidth: 620, margin: '10px auto 0', textAlign: 'left' }}>
            Sarah is a journalist and the James B. Steele Chair of Journalism Innovation at Temple University. Her writing about the news and information habits can be found at{' '}
            <a href="https://newsfix.ghost.io" target="_blank" rel="noopener noreferrer" style={{ color: '#5c7a5c', textDecoration: 'underline' }}>newsfix.ghost.io</a>.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 0 }}>
          <button style={tabStyle('diary')} onClick={() => setActiveTab('diary')}>📝 Diary</button>
          <button style={tabStyle('summary')} onClick={() => setActiveTab('summary')}>📊 Daily Summary</button>
          <button style={tabStyle('quiz')} onClick={() => setActiveTab('quiz')}>🧠 Emotion Quiz</button>
        </div>
        <div style={{ height: 2, background: '#e8f0e8', marginBottom: 24 }} />

        {activeTab === 'diary' && (
          <>
            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '28px 32px', marginBottom: 36 }}>
              <h2 style={{ fontSize: '1.1rem', color: '#3e5c3e', marginBottom: 20, borderBottom: '1px solid #e8f0e8', paddingBottom: 10 }}>
                {editingId !== null ? '✏️ Edit Entry' : '✏️ New Entry'}
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Need to Meet <em style={{ fontWeight: 'normal' }}>(What motivated you?)</em></label>
                  <input type="text" value={form.need} onChange={e => handleChange('need', e.target.value)} placeholder="e.g. boredom, work requirement, curiosity, distraction…" style={inputStyle} />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Information Source</label>
                  <input type="text" value={form.source} onChange={e => handleChange('source', e.target.value)} placeholder="e.g. Google, news source and article subject, podcast, social feed, RSS feed…" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Day</label>
                  <input type="date" value={form.day} onChange={e => handleChange('day', e.target.value)} style={inputStyle} />
                </div>
                <div />
                <div>
                  <label style={labelStyle}>Time On</label>
                  <input type="time" value={form.timeOn} onChange={e => handleChange('timeOn', e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Time Off</label>
                  <input type="time" value={form.timeOff} onChange={e => handleChange('timeOff', e.target.value)} style={inputStyle} />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Feeling of Satisfaction</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input type="range" min={1} max={10} value={form.satisfaction} onChange={e => handleChange('satisfaction', parseInt(e.target.value))} style={{ flex: 1, accentColor: '#5c7a5c' }} />
                    <span style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#3e5c3e', minWidth: 28, textAlign: 'center' }}>{form.satisfaction}</span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#7a7a7a', marginTop: 3 }}>{SAT_LABELS[form.satisfaction]}</div>
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={labelStyle}>Notes</label>
                  <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} placeholder="Any additional thoughts or observations…" rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22, alignItems: 'center' }}>
                {saved && <span style={{ fontSize: '0.85rem', color: '#5c7a5c', fontStyle: 'italic' }}>✓ Saved!</span>}
                <button onClick={() => { setForm(emptyForm()); setEditingId(null); }} style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: '#eee', color: '#7a7a7a', cursor: 'pointer', fontFamily: 'inherit' }}>Clear</button>
                <button onClick={handleSave} style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: '#5c7a5c', color: '#fff', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'inherit' }}>Save Entry</button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: '1.1rem', color: '#3e5c3e' }}>📋 Past Entries</h2>
              <span style={{ fontSize: '0.82rem', color: '#7a7a7a', background: '#e8f0e8', padding: '3px 10px', borderRadius: 20 }}>{entries.length} {entries.length === 1 ? 'entry' : 'entries'}</span>
            </div>
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>🔍</span>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search entries…" style={{ ...inputStyle, paddingLeft: 36 }} />
            </div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#aaa', fontStyle: 'italic' }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#aaa', fontStyle: 'italic', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                {entries.length === 0 ? 'No entries yet. Add your first one above!' : 'No entries match your search.'}
              </div>
            ) : filtered.map(entry => {
              const d = calcDuration(entry.timeOn, entry.timeOff);
              const sc = satColor(entry.satisfaction);
              return (
                <div key={entry.id} style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px 24px', marginBottom: 14, borderLeft: '4px solid #5c7a5c' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontSize: '0.82rem', color: '#7a7a7a' }}>
                      <span>📅 {fmtDate(entry.day)}</span>
                      {d && <span style={{ marginLeft: 10 }}>⏱ {d}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handleEdit(entry)} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', color: '#7a7a7a', cursor: 'pointer' }}>✏️ Edit</button>
                      <button onClick={() => handleDelete(entry.id)} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', color: '#7a7a7a', cursor: 'pointer' }}>🗑 Delete</button>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', fontSize: '0.9rem' }}>
                    {[['Need to Meet', entry.need], ['Information Source', entry.source], ['Time On', fmtTime(entry.timeOn)], ['Time Off', fmtTime(entry.timeOff)]].map(([lbl, val]) => (
                      <div key={lbl}>
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#aaa', marginBottom: 2 }}>{lbl}</div>
                        <div>{val || '—'}</div>
                      </div>
                    ))}
                    <div>
                      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#aaa', marginBottom: 2 }}>Satisfaction</div>
                      <span style={{ background: sc.bg, color: sc.text, borderRadius: 20, padding: '2px 10px', fontWeight: 'bold', fontSize: '0.85rem' }}>{entry.satisfaction}/10 — {SAT_LABELS[entry.satisfaction]}</span>
                    </div>
                    {entry.notes && (
                      <div style={{ gridColumn: '1/-1' }}>
                        <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#aaa', marginBottom: 2 }}>Notes</div>
                        <div>{entry.notes}</div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {activeTab === 'summary' && (
          <>
            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '28px 32px', marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: '1.1rem', color: '#3e5c3e', margin: 0 }}>📊 Summary for</h2>
                <input type="date" value={summaryDate} onChange={e => setSummaryDate(e.target.value)} style={{ ...inputStyle, width: 'auto' }} />
                <span style={{ fontSize: '0.85rem', color: '#7a7a7a', fontStyle: 'italic' }}>{fmtDate(summaryDate)}</span>
              </div>
            </div>
            {dayEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#aaa', fontStyle: 'italic', background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>No entries found for this day.</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
                  {[{ icon: '⏱', label: 'Total Time', value: fmtMins(totalMins) }, { icon: '📝', label: 'Entries', value: dayEntries.length }, { icon: '😊', label: 'Avg Satisfaction', value: avgSat ? `${avgSat}/10` : '—' }].map(({ icon, label, value }) => (
                    <div key={label} style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.6rem', marginBottom: 6 }}>{icon}</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#3e5c3e' }}>{value}</div>
                      <div style={{ fontSize: '0.78rem', color: '#7a7a7a', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 4 }}>{label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '24px 28px', marginBottom: 16 }}>
                  <h3 style={{ fontSize: '1rem', color: '#3e5c3e', marginBottom: 16 }}>🧠 Motivations (Need to Meet)</h3>
                  {Object.keys(motivations).length === 0 ? <p style={{ color: '#aaa', fontStyle: 'italic' }}>No motivations recorded.</p>
                  : Object.entries(motivations).map(([need, count]) => (
                    <div key={need} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <div style={{ flex: 1, fontSize: '0.95rem' }}>{need}</div>
                      <div style={{ background: '#e8f0e8', borderRadius: 20, padding: '2px 12px', fontSize: '0.82rem', color: '#3e5c3e', fontWeight: 'bold' }}>{count}×</div>
                    </div>
                  ))}
                </div>
                <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '24px 28px', marginBottom: 16 }}>
                  <h3 style={{ fontSize: '1rem', color: '#3e5c3e', marginBottom: 16 }}>🔗 Sources Consulted</h3>
                  {sources.length === 0 ? <p style={{ color: '#aaa', fontStyle: 'italic' }}>No sources recorded.</p>
                  : sources.map(s => <div key={s} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0', fontSize: '0.95rem' }}>{s}</div>)}
                </div>
                <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '24px 28px' }}>
                  <h3 style={{ fontSize: '1rem', color: '#3e5c3e', marginBottom: 16 }}>📋 All Entries for This Day</h3>
                  {dayEntries.map(entry => {
                    const d = calcDuration(entry.timeOn, entry.timeOff);
                    const sc = satColor(entry.satisfaction);
                    return (
                      <div key={entry.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 14, marginBottom: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: '0.82rem', color: '#7a7a7a' }}>{fmtTime(entry.timeOn)} — {fmtTime(entry.timeOff)} {d && `(${d})`}</span>
                          <span style={{ background: sc.bg, color: sc.text, borderRadius: 20, padding: '2px 10px', fontWeight: 'bold', fontSize: '0.82rem' }}>{entry.satisfaction}/10</span>
                        </div>
                        <div style={{ fontSize: '0.9rem', marginBottom: 2 }}><strong>Need:</strong> {entry.need || '—'}</div>
                        <div style={{ fontSize: '0.9rem', marginBottom: 2 }}><strong>Source:</strong> {entry.source || '—'}</div>
                        {entry.notes && <div style={{ fontSize: '0.85rem', color: '#7a7a7a', fontStyle: 'italic', marginTop: 4 }}>{entry.notes}</div>}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'quiz' && (
          <div style={{ margin: '0 -16px' }}>
            <EmotionQuizTab />
          </div>
        )}

      </div>
    </div>
  );
}
